import { DotV2 } from 'common/pill-v2/dot-v2.js';
import { PillDynamicWrapperV2 } from 'common/pill-v2/pill-dynamic-wrapper-v2.js';
import { PillPointer } from 'common/pill-v2/pill-pointer.js';
import { PillV2Label } from 'common/pill-v2/pill-v2-label.js';
import { MarkerV2SecondaryData } from 'common/pill-v2/pill-v2-secondary-data.js';
import { PillV2 } from 'common/pill-v2/pill-v2.js';
import { onClickStaticMapType } from 'components/map/map-controller/main/leaflet-click/click-updateSlot.js';
import { getMarkerV2ThemedPill, MarkerOptionsMap } from 'components/map/themes/pill-theme.js';
import { MarkerDisplaySize } from 'types/location.js';
import { MarkerPillFilterPoint } from 'types/markerTypesV2/m-v2-core.js';
import { IMarkerV2 } from 'types/markerTypesV2/m-v2-general.js';
import { predicatePopulatedString } from 'utilities/isType';
import { getMarkerV2TextPos } from './marker-v2';
import { defaultFilterMarkerOrder } from './utils';

const getFinalView = ({
  marker,
  radioActiveFilter,
}: {
  marker: IMarkerV2<MarkerPillFilterPoint>;
  radioActiveFilter: string[] | undefined;
}) => {
  const { view, views } = marker;

  // If no views or set as default use default marker view
  const useDefaultStationType = radioActiveFilter?.[0] === 'default' || !radioActiveFilter;
  // If no stationType, use the first view that exists
  if (views && useDefaultStationType) {
    const match = defaultFilterMarkerOrder.find((type) => !!views?.[type]);
    if (match) {
      const finalView = views[match];
      return { ...view, ...finalView };
    }
  }

  if (views && radioActiveFilter?.length) {
    const match = radioActiveFilter.find((type) => !!views?.[type]);
    if (match) {
      const finalView = views[match];
      return { ...view, ...finalView };
    }
  }
  return undefined;
};

export const mergeMarkerTags = ({ theme, tags = [] }: { theme?: string; tags?: string[] }) => {
  if (predicatePopulatedString(theme)) {
    return [theme, ...tags];
  }
  return tags;
};

export const MarkerFilterPillV2 = ({
  markerDisplaySize,
  marker,
  selected,
  mockHover,
  markerThemes,
  onClick,
  radioActiveFilter,
  markerTextOverride,
  staticMarkerId,
}: {
  marker: IMarkerV2<MarkerPillFilterPoint>;
  markerDisplaySize: MarkerDisplaySize;
  selected?: boolean;
  mockHover?: boolean;
  markerThemes?: MarkerOptionsMap;
  onClick?: onClickStaticMapType;
  radioActiveFilter?: string[];
  markerTextOverride?: string;
  staticMarkerId?: string;
}) => {
  const { position, label } = marker;

  const labelText = markerTextOverride || label?.text;
  const hasLabelSecondary = marker.generatedWarningsList?.length || label?.suffix?.length || 0 > 1;
  const hasLabel = !!labelText || hasLabelSecondary;

  const view = getFinalView({ marker, radioActiveFilter });
  if (view?.icon || view?.text || view?.theme) {
    const themeProps = getMarkerV2ThemedPill({
      tags: mergeMarkerTags({ theme: view?.theme, tags: marker?.tags }),
      meta: view?.meta ?? view?.status ?? view?.text,
      icon: view?.icon,
      markerThemes,
      defaultTheme: { border: '#ffffff', background: '#ffffff', text: '#0B1940' },
    });
    const isDot = markerDisplaySize === MarkerDisplaySize.Small;
    const textPos = getMarkerV2TextPos(marker);

    return (
      <PillDynamicWrapperV2
        displaySize={markerDisplaySize}
        selected={selected}
        mockHover={mockHover}
        staticPosition={position.staticPoint}
        labelPos={position.labelPos}
        markerType={marker.type}
        hasPoint={view?.hasPoint}
        offset={position.offset}
        isDot={isDot}
        onClick={onClick}
        textPos={textPos}
        staticMarkerId={staticMarkerId}
      >
        {isDot ? (
          <DotV2 themeProps={themeProps} />
        ) : (
          <>
            <PillV2
              icons={[view?.icon ?? themeProps?.icon]}
              className={`pill--border u-posRel ${marker.view?.pillClass ?? ''}`}
              label={view?.text}
              themeProps={themeProps}
            />
            <PillPointer
              border={themeProps?.border}
              labelPos={position.labelPos}
              hasPoint={view?.hasPoint}
              id={view?.text}
            />
            {hasLabel && (
              <PillV2Label
                selected={selected}
                label={labelText}
                labelTextHover={label?.textHover}
                displaySize={markerDisplaySize}
                mockHover={mockHover}
                textPos={textPos}
              >
                <MarkerV2SecondaryData secondaryData={label?.suffix} warningsList={marker.generatedWarningsList} />
              </PillV2Label>
            )}
          </>
        )}
      </PillDynamicWrapperV2>
    );
  }
};
